/**
 * Scope-filter — прикладные поля вне scope (другие поля панели, чужие
 * field'ы pagesData) дропаются, кроме:
 * - ключей уже в `out` (En._serialize / явный putData);
 * - aux перезагружаемого элемента;
 * - ALWAYS_PASS / `$…`;
 * - Identificator-таблиц (`pickIdentificatorTableFieldsForReload`) — Swing
 *   `Identificator.getData` едет в каждый `reloadElement` (2615 `matchedIds`).
 */

import { isFieldUniDataTable } from '@/common/utils/jsonUniData'
import { normalizeFormElementType } from '@/common/utils/formElementType'

/** Платформенные глобальные поля и document-поля, которые всегда пробрасываем. */
const ALWAYS_PASS_KEYS = new Set<string>([
  '_id',
  '_typeId',
  '_versionId',
  '_typeVersionId',
  '_typeVersion',
  'ProcessInstances',
  'exception',
  'doActionId',
  'openPage',
  'elementConditions',
  'reloadActions',
])

function isAlwaysPass(key: string): boolean {
  if (!key) return false
  if (key.startsWith('$')) return true
  return ALWAYS_PASS_KEYS.has(key)
}

function isAuxOfReloadedElement(key: string, reloadedFieldName: string): boolean {
  if (!reloadedFieldName) return false
  return (
    key === reloadedFieldName ||
    key.startsWith(`${reloadedFieldName}_`) ||
    key.startsWith(`${reloadedFieldName}__`)
  )
}


export function isInlineTableReloadContext(
  tableFieldName: string,
  fields: Record<string, unknown> | null | undefined,
): boolean {
  const name = String(tableFieldName ?? '').trim()
  if (!name || !fields || typeof fields !== 'object') {
    return false
  }
  return (
    Object.prototype.hasOwnProperty.call(fields, `${name}__rowNum`) ||
    Object.prototype.hasOwnProperty.call(fields, `${name}__column`)
  )
}

function pickInlineTableReloadAuxFields(
  out: Record<string, unknown>,
  tableFieldName: string,
): Record<string, unknown> {
  const name = String(tableFieldName ?? '').trim()
  if (!name) return {}
  const prefix = `${name}__`
  const aux: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(out)) {
    if (key.startsWith(prefix)) {
      aux[key] = value
    }
  }
  return aux
}

export function buildInlineTableReloadPayload(
  out: Record<string, unknown>,
  tableFieldName: string,
  ...scalarLayers: Array<Record<string, unknown> | null | undefined>
): Record<string, unknown> {
  const formScalars: Record<string, unknown> = {}
  for (const layer of scalarLayers) {
    if (!layer || typeof layer !== 'object') continue
    Object.assign(formScalars, layer)
  }
  return { ...formScalars, ...pickInlineTableReloadAuxFields(out, tableFieldName) }
}

export function filterReloadWidePoolByScope(
  widePool: Record<string, unknown> | null | undefined,
  outScope: Record<string, unknown> | null | undefined,
  reloadedFieldName: string,
): Record<string, unknown> {
  if (!widePool) return {}
  const allowedFromOut = outScope && typeof outScope === 'object' ? outScope : {}
  const omitInlineTableBody = isInlineTableReloadContext(reloadedFieldName, allowedFromOut)
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(widePool)) {
    if (omitInlineTableBody && key === reloadedFieldName) {
      continue
    }
    if (
      Object.prototype.hasOwnProperty.call(allowedFromOut, key) ||
      isAuxOfReloadedElement(key, reloadedFieldName) ||
      isAlwaysPass(key)
    ) {
      out[key] = value
    }
  }
  return out
}

/**
 * Swing `Identificator.getData` — скрытая таблица-фильтр (не body TABLE).
 * WUI-507 режет sibling tables из wide pool; Identificator оставляем.
 */
export function pickIdentificatorTableFieldsForReload(
  elements: Record<string, unknown> | null | undefined,
  sources: Array<Record<string, unknown> | null | undefined>,
  reloadedFieldName: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!elements || typeof elements !== 'object') {
    return out
  }
  const skip = String(reloadedFieldName ?? '').trim()
  for (const el of Object.values(elements)) {
    if (!el || typeof el !== 'object') continue
    const rec = el as {
      fieldName?: unknown
      name?: unknown
      type?: unknown
      elementType?: unknown
      hooks?: { getData?: () => unknown }
    }
    const type = String(
      normalizeFormElementType(rec) ?? rec.elementType ?? rec.type ?? '',
    ).toUpperCase()
    if (type !== 'IDENTIFICATOR') continue
    const name = String(rec.fieldName ?? rec.name ?? '').trim()
    if (!name || name === skip) continue

    let value: unknown
    const hookData = rec.hooks?.getData?.()
    if (hookData && typeof hookData === 'object' && !Array.isArray(hookData)) {
      value = (hookData as Record<string, unknown>)[name]
    }
    if (value == null) {
      for (const src of sources) {
        if (!src || typeof src !== 'object') continue
        if (Object.prototype.hasOwnProperty.call(src, name) && src[name] != null) {
          value = src[name]
          break
        }
      }
    }
    if (value != null && isFieldUniDataTable(value)) {
      out[name] = value
    }
  }
  return out
}
